/**
 * Consent-gated product analytics (GA4 via Firebase Analytics).
 *
 * Analytics is not initialised until the user accepts analytics cookies, so a
 * user who rejects (or never answers) gets no GA cookies and sends no events.
 * Event params must never carry titles, URLs, content or email addresses.
 */
import {
  getAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  setConsent,
  type Analytics,
} from 'firebase/analytics';
import { clearAnalyticsCookies, readConsent, type ConsentState } from './consent';

export type AnalyticsEventName =
  | 'login_started'
  | 'onboarding_step'
  | 'conversion_submitted'
  | 'audio_play'
  | 'feed_url_copied'
  | 'share_link_copied'
  | 'checkout_started';

export type AnalyticsParams = Record<string, string | number | boolean>;

// Analytics never runs against the Auth emulator (local dev and E2E), where
// there is no real Firebase app to report to, or in builds without a
// measurement ID. Read from env directly so importing this module doesn't
// load the Firebase config — that only happens once consent is granted.
let instance: Promise<Analytics | null> | null = null;

/** True when this build can run analytics at all (so the banner is relevant). */
export function isAnalyticsAvailable(): boolean {
  return (
    Boolean(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) &&
    !import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL
  );
}

/** Initialise analytics once, and only after consent has been granted. */
export function initAnalytics(): Promise<Analytics | null> {
  if (!isAnalyticsAvailable() || readConsent() !== 'granted') return Promise.resolve(null);
  if (!instance) {
    instance = isSupported()
      .then(async (supported) => {
        if (!supported) return null;
        const { app } = await import('../config/firebase');
        // Consent Mode v2: analytics storage only — advertising stays denied.
        setConsent({
          analytics_storage: 'granted',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
        const analytics = getAnalytics(app);
        setAnalyticsCollectionEnabled(analytics, true);
        return analytics;
      })
      .catch(() => null);
  }
  return instance;
}

/** Apply a new consent choice to a running page. */
export async function applyConsent(state: ConsentState): Promise<void> {
  if (state === 'granted') {
    await initAnalytics();
    return;
  }
  const analytics = instance ? await instance : null;
  if (analytics) {
    setConsent({ analytics_storage: 'denied' });
    setAnalyticsCollectionEnabled(analytics, false);
  }
  clearAnalyticsCookies();
}

/** Log a product event. A no-op without consent. */
export function trackEvent(name: AnalyticsEventName, params?: AnalyticsParams): void {
  if (!isAnalyticsAvailable() || readConsent() !== 'granted') return;
  void initAnalytics().then((analytics) => {
    if (analytics) logEvent(analytics, name, params);
  });
}

/** Test hook: forget the cached analytics instance. */
export function resetAnalyticsForTests(): void {
  instance = null;
}
